import {Suspense, useCallback, useState} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import clsx from 'clsx';
import {diApi} from '@di-shared/api/di';
import {CreatedMaintenanceRecordRequest, UpdatedMaintenanceRecordRequest} from '@di-shared/api/di/__generated';
import FocusToScroll from '@di-shared/features/common/FocusToScroll';
import Header from '@di-shared/features/common/Header';
import HeaderBackButton from '@di-shared/features/common/HeaderBackButton';
import InputText from '@di-shared/features/common/InputText';
import useApiError from '@di-shared/features/common/useApiError';
import useCcWebviewUtilsHelper from '@di-shared/features/common/useCcWebviewUtilsHelper';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import useSestName from '@di-shared/features/diag/sest/useSestName';
import useOdometer from '@di-shared/features/mileage/useOdometer';
import {ROUTE_PATHS} from '@di-shared/routes';
import {formatNumberWithCommas} from '@di-shared/utils';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import useUnitConverter from '@di-shared/utils/useUnitConverter';
import {ccWebviewUtils} from '@shared/cci/features/webviewUtils';
import {dateFormat, parseDateWithFormat} from '@shared/dateUtils';
import {getQueryKey} from '@shared/queryKeyMap';
import usePendingHandler from '@shared/usePendingHandler';
import {WebviewUtils} from '@shared/webviewUtils';
import {useQueryClient, useSuspenseQuery} from '@tanstack/react-query';

export const ERR_4090_CONFLICT = 4090;

interface DiagExchangeHistoryDetailEditProps {
  vin: string;
  historyId: string;
}

function DiagExchangeHistoryDetailEdit(props: DiagExchangeHistoryDetailEditProps) {
  const {t} = useInferableTranslation();
  const {backOrClose} = useCcWebviewUtilsHelper();
  return (
    <ErrorBoundary
      fallbackRender={() => {
        WebviewUtils.showToast(t('T20096'));
        backOrClose();
        return null;
      }}
    >
      <Suspense fallback={<Loading />}>
        <Inner {...props} />
      </Suspense>
    </ErrorBoundary>
  );
}

function Inner({vin, historyId}: DiagExchangeHistoryDetailEditProps) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {defaultDistanceSymbol, revertDistanceValue} = useUnitConverter();
  const {odometer} = useOdometer(vin);
  const {getErrorCode} = useApiError();
  const {openConfirm, backOrClose, openAlert} = useCcWebviewUtilsHelper();
  const {sestNameLabels} = useSestName();
  const queryClient = useQueryClient();
  const {data} = useSuspenseQuery({
    queryKey: [getQueryKey(diApi.maintenanceRecordController.getMaintenanceRecord), vin, historyId],
    queryFn: () => diApi.maintenanceRecordController.getMaintenanceRecord({vin, recordId: +historyId}),
  });

  // 폼 상태 통합 관리
  const [form, setForm] = useState<{
    exchangeDate: Date | null;
    enteredOdometer: string;
  }>({
    exchangeDate: parseDateWithFormat(data.data.replaceDate),
    enteredOdometer: data.data.replaceOdometer.toString(),
  });

  const [saveExchangeHistory] = usePendingHandler(
    async () => {
      if (!form.exchangeDate) {
        return;
      }
      await diApi.maintenanceRecordController.updateMaintenanceRecord({
        vin,
        recordId: +historyId,
        requestBody: {
          vin,
          sestCode: data.data.sestCode as UpdatedMaintenanceRecordRequest.sestCode,
          replaceOdometer: revertDistanceValue(+form.enteredOdometer),
          replaceDate: dateFormat(form.exchangeDate, 'yyyyMMdd'),
          source: data.data.source,
          isActive: data.data.isActive,
        },
      });
      await queryClient.invalidateQueries({
        queryKey: [getQueryKey(diApi.maintenanceRecordController.getMaintenanceRecord), vin, historyId],
      });
      backOrClose();
      WebviewUtils.showToast(t('T30589'));
    },
    async (error: any) => {
      /**
       * [2025-11-25:Jeyoung Lee]
       * 4090 이미 등록된 레코드에 대한 에러코드
       */
      if (getErrorCode(error) === ERR_4090_CONFLICT) {
        const isConfirm = await openConfirm({
          title: t('T30590'),
          message: t('T30591'),
          okText: t('T30592'),
          cancelText: t('T30593'),
        });
        if (isConfirm) {
          backOrClose();
        }
        return;
      }

      await openAlert({
        title: t('T30594'),
        message: t('T10160_$tel', {tel: import.meta.env.VITE_EMERGENCY_TEL_BRAND}),
      });
    }
  );

  const [deleteExchangeHistory] = usePendingHandler(
    async () => {
      const isConfirm = await openConfirm({
        title: t('T30595'),
        message: t('T30596'),
        okText: t('T10154'),
        cancelText: t('T20182'),
      });
      if (!isConfirm) {
        return;
      }
      await diApi.maintenanceRecordController.deleteMaintenanceRecord({vin, recordId: +historyId});
      await queryClient.invalidateQueries({
        queryKey: [getQueryKey(diApi.maintenanceRecordController.getMaintenanceRecord), vin, historyId],
      });
      backOrClose();
      WebviewUtils.showToast(t('T30597'));
    },
    async () => {
      await openAlert({
        title: t('T30598'),
        message: t('T10160_$tel', {tel: import.meta.env.VITE_EMERGENCY_TEL_BRAND}),
      });
    }
  );

  const today = new Date();

  const selectExchangeDate = useCallback(async () => {
    const selectedDate = await ccWebviewUtils.openBottomSheetWebViewUrl<Date | null>(
      ROUTE_PATHS.BOTTOM_SHEET_DIAG_EXCHANGE_REGISTER_DATE_PICKER.getFullPath(),
      {selectedDate: form.exchangeDate}
    );
    if (selectedDate) {
      setForm((prev) => ({...prev, exchangeDate: selectedDate}));
    }
  }, [form.exchangeDate]);

  const updateEnteredOdometer = useCallback((odometerValue: string) => {
    setForm((prev) => ({...prev, enteredOdometer: odometerValue}));
  }, []);

  // 에러 상태 계산
  const isExchangeDateError = !!form.exchangeDate && form.exchangeDate > today;
  const isOdometerError = !!form.enteredOdometer && odometer !== undefined && +form.enteredOdometer > odometer;

  return (
    <div className="wrap">
      <Header left={<HeaderBackButton />}>
        <h1 className="headline_xxsmall nav_center_title">{t('T30599')}</h1>
      </Header>
      <main
        id="content"
        className="content page_car_management"
      >
        <section className="section section_exchange_register">
          <div className="section_heading type_main">
            <h2 className="section_heading_title">{sestNameLabels[data.data.sestCode as CreatedMaintenanceRecordRequest.sestCode]} </h2>
            <p className="section_heading_desc">{t('T30600')}</p>
          </div>
          <div className="box_outline_group">
            {/* 교체일 */}
            <div
              className={clsx('field_box', {
                input_completed: !!form.exchangeDate,
                input_error: isExchangeDateError,
              })}
            >
              <label
                htmlFor="exchangeDate"
                className="label"
              >
                {t('T30158')}
              </label>
              <div className="input_box">
                <input
                  id="exchangeDate"
                  className="input input_date"
                  placeholder={t('T30159')}
                  readOnly
                  required
                  value={form.exchangeDate ? CldrDatetimeFormatter.format(form.exchangeDate, 'yMMMd')?.toString() : ''}
                  onClick={selectExchangeDate}
                  onChange={() => {}}
                />
              </div>
            </div>
            {isExchangeDateError && <p className="txt_error body_small_r">{t('T30160')}</p>}

            {/* 누적 주행거리 */}
            <div
              className={clsx('field_box', {
                input_completed: !!form.enteredOdometer,
                input_error: isOdometerError,
              })}
            >
              <label
                htmlFor="exchangeDistance"
                className="label"
              >
                {t('T30163')}
              </label>
              <div className="input_box">
                <FocusToScroll>
                  <InputText
                    initValue={form.enteredOdometer ? formatNumberWithCommas(form.enteredOdometer) : ''}
                    inputMode="numeric"
                    onChange={updateEnteredOdometer}
                    id="exchangeDistance"
                    className="input input_text"
                    placeholder="0"
                    regex={/[^0-9]/g}
                    required={true}
                  />
                </FocusToScroll>
                <span className="input_unit">{defaultDistanceSymbol}</span>
              </div>
            </div>
            {isOdometerError && <p className="txt_error body_small_r">{t('T30162')}</p>}
          </div>
        </section>
        <div className="divider divider_thick"></div>
        <section className="section section_notice">
          <ul className="notice_list">
            <li className="notice_item">{t('T30582')}</li>
            <li className="notice_item">{t('T30601')}</li>
          </ul>
        </section>
      </main>
      <div className="floating_action_area">
        <button
          type="button"
          className="btn btn_large btn_filled btn_full"
          disabled={form.exchangeDate === null || form.enteredOdometer === '' || isExchangeDateError || isOdometerError}
          onClick={saveExchangeHistory}
        >
          {t('T30049')}
        </button>
        <button
          type="button"
          className="btn btn_large btn_outline btn_full"
          onClick={deleteExchangeHistory}
        >
          {t('T10154')}
        </button>
      </div>
    </div>
  );
}

function Loading() {
  const {t} = useInferableTranslation();
  const {defaultDistanceSymbol} = useUnitConverter();
  return (
    <div className="wrap">
      <Header left={<HeaderBackButton />}>
        <h1 className="headline_xxsmall nav_center_title">{t('T30599')}</h1>
      </Header>
      <main
        id="content"
        className="content page_car_management"
      >
        <section className="section section_exchange_register">
          <div className="section_heading type_main">
            <h2 className="section_heading_title skeleton">&nbsp;</h2>
            <p className="section_heading_desc">{t('T30600')}</p>
          </div>
          <div className="box_outline_group">
            {/* 교체일 */}
            <div className={clsx('field_box')}>
              <label
                htmlFor="exchangeDate"
                className="label"
              >
                {t('T30158')}
              </label>
              <div className="input_box">
                <input
                  id="exchangeDate"
                  className="input input_date"
                  placeholder={t('T30159')}
                  readOnly
                  required
                  value={''}
                  onClick={() => {}}
                  onChange={() => {}}
                />
              </div>
            </div>

            {/* 누적 주행거리 */}
            <div className={clsx('field_box')}>
              <label
                htmlFor="exchangeDistance"
                className="label"
              >
                {t('T30163')}
              </label>
              <div className="input_box">
                <FocusToScroll>
                  <InputText
                    initValue={''}
                    inputMode="numeric"
                    onChange={() => {}}
                    id="exchangeDistance"
                    className="input input_text"
                    placeholder="0"
                    regex={/[^0-9]/g}
                    required={true}
                  />
                </FocusToScroll>
                <span className="input_unit">{defaultDistanceSymbol}</span>
              </div>
            </div>
          </div>
        </section>
        <div className="divider divider_thick"></div>
        <section className="section section_notice">
          <ul className="notice_list">
            <li className="notice_item">{t('T30582')}</li>
            <li className="notice_item">{t('T30601')}</li>
          </ul>
        </section>
      </main>
      <div className="floating_action_area">
        <button
          type="button"
          className="btn btn_large btn_filled btn_full"
          disabled
          onClick={() => {}}
        >
          {t('T30049')}
        </button>
        <button
          type="button"
          className="btn btn_large btn_outline btn_full"
          onClick={() => {}}
          disabled
        >
          {t('T10154')}
        </button>
      </div>
    </div>
  );
}

export default DiagExchangeHistoryDetailEdit;
